import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Business,
  Email,
  Phone,
  LocationOn,
  Image,
  Close,
  Description,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { registerOrganization } from "../redux/slices/authSlice";

const ORGANIZATION_TYPES = [
  { value: "academie_sportive", label: "Académie sportive" },
  { value: "agence_evenementiel", label: "Agence d’événementiel sportif" },
  { value: "association_sportive", label: "Association sportive" },
  { value: "association_socio_sportive", label: "Association socio-sportive" },
  { value: "autre", label: "Autre (à préciser)" },
  { value: "centre_formation", label: "Centre de formation sportive" },
  { value: "centre_loisirs", label: "Centre de loisirs" },
  { value: "club_amateur", label: "Club amateur" },
  { value: "club_professionnel", label: "Club professionnel" },
  {
    value: "collectivite_territoriale",
    label: "Collectivité territoriale (mairie, département, région)",
  },
  { value: "comite_departemental", label: "Comité départemental" },
  { value: "comite_regional", label: "Comité régional" },
  { value: "complexe_sportif", label: "Complexe sportif" },
  { value: "ecole_primaire", label: "École primaire" },
  { value: "entreprise_privee", label: "Entreprise privée" },
  {
    value: "enseignement_secondaire",
    label: "Établissement d’enseignement secondaire (collège / lycée)",
  },
  {
    value: "enseignement_superieur",
    label: "Établissement d’enseignement supérieur",
  },
  { value: "federation_sportive", label: "Fédération sportive" },
  { value: "fondation", label: "Fondation" },
  { value: "institution_publique", label: "Institution publique" },
  { value: "ligue_sportive", label: "Ligue sportive" },
  { value: "maison_quartier", label: "Maison de quartier" },
  { value: "organisme_formation", label: "Organisme de formation" },
  { value: "organisme_medico_social", label: "Organisme médico-social" },
  { value: "plateforme_digitale", label: "Plateforme sportive digitale" },
  { value: "salle_sport", label: "Salle de sport / fitness" },
  { value: "service_jeunesse", label: "Service jeunesse et sports" },
  { value: "insertion_sport", label: "Structure d’insertion par le sport" },
  { value: "handisport", label: "Structure spécialisée handisport" },
  { value: "sport_adapte", label: "Structure spécialisée sport adapté" },
  { value: "suaps", label: "Structure universitaire (SUAPS)" },
];

const OrganizationRegisterForm = ({ loading }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    location: "",
    type: "",
    otherType: "",
    description: "",
    logo: null,
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        setErrors((prev) => ({
          ...prev,
          logo: "La taille du fichier ne doit pas dépasser 5MB",
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, logo: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Clear error
      if (errors.logo) {
        setErrors((prev) => ({ ...prev, logo: "" }));
      }
    }
  };

  const removeLogo = () => {
    setFormData((prev) => ({ ...prev, logo: null }));
    setLogoPreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Le nom de l'organisation est requis";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (!formData.password) {
      newErrors.password = "Le mot de passe est requis";
    } else if (formData.password.length < 6) {
      newErrors.password =
        "Le mot de passe doit contenir au moins 6 caractères";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Le téléphone est requis";
    }

    if (!formData.location.trim()) {
      newErrors.location = "La localisation est requise";
    }

    if (!formData.type) {
      newErrors.type = "Veuillez sélectionner un type d'organisation";
    }

    if (formData.type === "autre" && !formData.otherType.trim()) {
      newErrors.otherType = "Veuillez préciser le type d'organisation";
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms =
        "Vous devez accepter les conditions d'utilisation";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      const submissionData = {
        ...formData,
        type: formData.type === "autre" ? "autre" : formData.type,
        description:
          formData.type === "autre"
            ? `Type: ${formData.otherType}\n\n${formData.description}`
            : formData.description,
      };

      delete submissionData.otherType;

      dispatch(registerOrganization(submissionData));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="register-form">
      <TextField
        fullWidth
        label="Nom de l'organisation"
        value={formData.name}
        onChange={handleInputChange("name")}
        error={!!errors.name}
        helperText={errors.name}
        placeholder="Nom de votre organisation"
        InputProps={{
          startAdornment: <Business sx={{ color: "action.active", mr: 1 }} />,
        }}
        className="form-field"
      />

      <TextField
        fullWidth
        label="Email professionnel"
        type="email"
        value={formData.email}
        onChange={handleInputChange("email")}
        error={!!errors.email}
        helperText={errors.email}
        placeholder="contact@organisation.com"
        InputProps={{
          startAdornment: <Email sx={{ color: "action.active", mr: 1 }} />,
        }}
        className="form-field"
      />

      <Box className="form-row">
        <TextField
          fullWidth
          label="Mot de passe"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange("password")}
          error={!!errors.password}
          helperText={errors.password}
          placeholder="Minimum 6 caractères"
          className="form-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirmer le mot de passe"
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={handleInputChange("confirmPassword")}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          placeholder="Répétez votre mot de passe"
          className="form-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className="form-row">
        <TextField
          fullWidth
          label="Téléphone"
          value={formData.phone}
          onChange={handleInputChange("phone")}
          error={!!errors.phone}
          helperText={errors.phone}
          placeholder="01 23 45 67 89"
          InputProps={{
            startAdornment: <Phone sx={{ color: "action.active", mr: 1 }} />,
          }}
          className="form-field"
        />

        <TextField
          fullWidth
          label="Ville"
          value={formData.location}
          onChange={handleInputChange("location")}
          error={!!errors.location}
          helperText={errors.location}
          placeholder="Ville"
          InputProps={{
            startAdornment: (
              <LocationOn sx={{ color: "action.active", mr: 1 }} />
            ),
          }}
          className="form-field"
        />
      </Box>

      <FormControl fullWidth className="form-field" error={!!errors.type}>
        <InputLabel>Type d'organisation</InputLabel>
        <Select
          value={formData.type}
          onChange={handleInputChange("type")}
          label="Type d'organisation"
        >
          {ORGANIZATION_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
        {errors.type && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, ml: 1.75 }}
          >
            {errors.type}
          </Typography>
        )}
      </FormControl>

      {formData.type === "autre" && (
        <TextField
          fullWidth
          label="Précisez le type d'organisation"
          value={formData.otherType}
          onChange={handleInputChange("otherType")}
          error={!!errors.otherType}
          helperText={
            errors.otherType || "Veuillez indiquer votre type de structure"
          }
          placeholder="Ex: Association culturelle, Groupe informel..."
          className="form-field"
          sx={{ mt: 2 }}
        />
      )}

      <TextField
        fullWidth
        label="Description (optionnel)"
        multiline
        rows={3}
        value={formData.description}
        onChange={handleInputChange("description")}
        placeholder="Décrivez brièvement votre organisation, vos activités et vos objectifs..."
        InputProps={{
          startAdornment: (
            <Description
              sx={{
                color: "action.active",
                mr: 1,
                alignSelf: "flex-start",
                mt: 1,
              }}
            />
          ),
        }}
        className="form-field"
        helperText="Maximum 500 caractères"
        inputProps={{ maxLength: 500 }}
      />

      <Box className="form-field">
        <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
          Logo de l'organisation (optionnel)
        </Typography>

        <Box
          className={`file-upload-container ${formData.logo ? "has-file" : ""}`}
          onClick={() => document.getElementById("logo-upload").click()}
        >
          <input
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />

          {logoPreview ? (
            <Box className="file-preview">
              <img
                src={logoPreview}
                alt="Aperçu du logo"
                style={{ borderRadius: "8px" }}
              />
              <Box>
                <Typography variant="body2" color="primary">
                  {formData.logo?.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeLogo();
                  }}
                  sx={{ mt: 0.5 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              <Image sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body2" className="file-upload-text">
                Cliquez pour ajouter un logo
              </Typography>
              <Typography variant="caption" className="file-upload-text">
                JPG, PNG - Max 5MB
              </Typography>
            </>
          )}
        </Box>

        {errors.logo && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: "block" }}
          >
            {errors.logo}
          </Typography>
        )}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.acceptTerms}
            onChange={handleInputChange("acceptTerms")}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            J'accepte les{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              conditions d'utilisation
            </a>{" "}
            et la{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              politique de confidentialité
            </a>
          </Typography>
        }
        className="terms-checkbox"
        sx={{ alignItems: "flex-start" }}
      />

      {errors.acceptTerms && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          {errors.acceptTerms}
        </Typography>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        className="register-button"
        startIcon={
          loading ? <CircularProgress size={20} color="inherit" /> : null
        }
      >
        {loading ? "Inscription en cours..." : "S'inscrire comme organisation"}
      </Button>
    </Box>
  );
};

export default OrganizationRegisterForm;
